import { useRef, useState } from 'react';

import useNavigateWithTransition from '@/hooks/useNavigateWithTransition';
import useTranslations from '@/i18n/useTranslations';
import ArtworksOfArtist from '@/pages/profile/artists/artworks-of-artist';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import FollowButton from '@/shared-components/buttons/components/FollowButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import ArtistVerifiedIcon from '@/shared-components/icons/components/user-profile/ArtistVerifiedIcon';
import Text from '@/shared-components/text/components/Text';

import './styles/HomeUserCard.css';

export default function HomeUserCard({ userData, style = {}, infiniteScrollObserver }) {
  const tHome = useTranslations('common.shared.home');
  const navigateWithTransition = useNavigateWithTransition();
  const imageWrapperRef = useRef(null);
  const [openArtworksModal, setOpenArtworksModal] = useState(false);

  const handleVisitProfile = () => {
    setOpenArtworksModal(true);
  };

  return (
    <>
      <div
        className="home-user-card element-non-selectable"
        style={style}
        onClick={() => {
          if (userData.user_type === "artist" || userData.user_type === "gallery") {
            if (imageWrapperRef.current) {
              imageWrapperRef.current.style.viewTransitionName = `user_${userData._id}_animation`;
            }
            navigateWithTransition(`/user/${userData.user_username}`, { state: { from: true } });
          } else if (userData.user_type === "gallery_artist") {
            handleVisitProfile();
          }
        }}
        ref={infiniteScrollObserver}
      >
        <div className="home-user-card__image-wrapper" ref={imageWrapperRef}>
          <UserProfileImage
            className="home-user-card__image"
            image={userData.user_image?.image_id}
            typeOfProfile={userData.user_type}
          />
        </div>
        <div className="home-user-card__info">
          <Text
            className="home-user-card__name dots-on-overflow">
            {userData.user_name || userData.artist_name}
            {(userData.user_type === "artist" && userData.user_flags?.suarteroad_completed) && (
              <ArtistVerifiedIcon className="home-user-card__artist-verified"/>
            )}
          </Text>

          {userData.user_type === "gallery" && (
            <Text className="home-user-card__subtitle dots-on-overflow">
              {[userData?.gallery_address?.city, userData?.gallery_address?.country].filter(Boolean).join(', ') || '-'}
            </Text>
          )}

          {userData.user_type === "artist" && (
            <Text className="home-user-card__subtitle dots-on-overflow">
              {userData?.artist_nationality ?? '-'}, {userData?.artist_birth ?? '-'}
            </Text>
          )}

          {userData.user_type === "gallery_artist" && (
            <Text className="home-user-card__subtitle dots-on-overflow">
              {userData?.artist_nationality}, {userData?.artist_birth}
            </Text>
          )}

          {(userData.user_type === "artist" || userData.user_type === "gallery") && (
            <div className="home-user-card__follow-action">
              <FollowButton
                className="home-user-card__action"
                userId={userData?._id}
                isFollowingMe={userData?.is_following_me}
                imFollowing={userData?.im_following}
                userName={userData?.user_username}
              />
            </div>
          )}

          {userData.user_type === "gallery_artist" && (
            <ContinueButton
              onClick={(e) => { e.stopPropagation(); handleVisitProfile(e, userData); }}
              className="home-user-card__action home-user-card__visit-button"
            >
              {tHome('visit_profile')}
            </ContinueButton>
          )}
        </div>
      </div>
      {openArtworksModal && (
        <ArtworksOfArtist
          open={openArtworksModal}
          close={() => setOpenArtworksModal(false)}
          artist={userData}
          internal={false}
          showHeader={false}
          cacheKey={`artworks_of_${userData._id}_in_search`}
          queryEndpoint={`/galleryartist/${userData._id}/artworks`}
        />
      )}
    </>
  );
}
